import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { parseDate } from '@superdispatch/dates';
import { ExitTransitionPlaceholder, Image, Stack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useEffect, useMemo } from 'react';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { LocalStore, useLocalStore } from 'shared/helpers/Store';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import styled from 'styled-components';
import insuranceExpiringDark from '../assets/insurance-expiring-dark.png';
import insuranceExpiring from '../assets/insurance-expiring.png';
import { DateTime } from 'luxon';
import { trackEvent } from 'shared/helpers/Analytics';

const MainIllustration = styled(Image)`
  width: 150px;
`;

const CenteredDialogActions = styled(DialogActions)`
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;

  &.MuiDialogActions-spacing > :not(:first-child) {
    margin-left: 0;
  }

  ${({ theme }) => theme.breakpoints.down('xs')} {
    flex-direction: column-reverse;
  }
`;

const INSURANCE_EXPIRING_DIALOG_SEEN = 'has_seen_insurance_expiring_dialog';

function LoadboardInsuranceExpiringDialogContent() {
  useEffect(() => {
    trackEvent('Carrier Viewed Insurance Expiring Dialog');
  }, []);

  function saveDialogSeen() {
    LocalStore.set(INSURANCE_EXPIRING_DIALOG_SEEN, DateTime.local().toString());
  }

  return (
    <>
      <DialogContent>
        <Box maxWidth="416px">
          <Stack align="center" space="large">
            <MainIllustration
              alt="Insurance expiring illustration"
              src={insuranceExpiring}
              srcDark={insuranceExpiringDark}
            />
            <Stack align="center">
              <Typography align="center" variant="h2">
                Act Now: Insurance Expiring Soon!
              </Typography>
              <Typography align="center">
                Your Insurance is about to expire. Renew it fast to keep your
                verified status and access to Super Loadboard
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <CenteredDialogActions>
        <Button variant="neutral" onClick={saveDialogSeen}>
          I’ll do it Later
        </Button>
        <Button
          onClick={() => {
            saveDialogSeen();
            if (MobileAppBridge.isInjected()) {
              MobileAppBridge.openProfile();
            } else {
              openExternalURL(
                '/tms/profile/cargo_insurance?utm_medium=Insurance Expiring Dialog',
              );
            }
          }}
        >
          Upload Renewal
        </Button>
      </CenteredDialogActions>
    </>
  );
}

export function LoadboardInsuranceExpiringDialog() {
  const { data: settings } = useCarrierSettings();

  const expiringSeenValue = useLocalStore(INSURANCE_EXPIRING_DIALOG_SEEN);
  const isInsuranceExpiringSeen = useMemo(() => {
    return parseDate(expiringSeenValue).diffNow().as('days') > -1;
  }, [expiringSeenValue]);

  const daysLeft = useMemo(() => {
    return parseDate(settings?.carrier.cargo_insurance_expiration_date)
      .diffNow()
      .as('days');
  }, [settings]);

  const isInsuranceExpiringOpen =
    !isInsuranceExpiringSeen &&
    daysLeft < 31 &&
    settings?.carrier.verification_status === 'approved' &&
    !settings.carrier.is_insurance_renewal_initiated;

  return (
    <Dialog open={isInsuranceExpiringOpen}>
      <ExitTransitionPlaceholder in={isInsuranceExpiringOpen}>
        {isInsuranceExpiringOpen && <LoadboardInsuranceExpiringDialogContent />}
      </ExitTransitionPlaceholder>
    </Dialog>
  );
}
